import type {
  CalibrationDeviceBranchState,
  CalibrationDeviceTreeEdge,
  CalibrationDeviceTreeNode,
  CalibrationDeviceTreeRow,
} from '@/features/calibration/types/calibrationDeviceTree';

export function calibrationDeviceBranchState({
  hasData,
  isError,
  isPending,
  deviceCount,
}: {
  hasData: boolean;
  isError: boolean;
  isPending: boolean;
  deviceCount: number;
}): CalibrationDeviceBranchState {
  if (!hasData && isPending) return 'pending';
  if (!hasData && isError) return 'failed';
  if (deviceCount === 0) return 'empty';
  return 'ready';
}

function calibrationDeviceTreeEdge(index: number, count: number): CalibrationDeviceTreeEdge {
  if (count === 1) return 'only';
  if (index === 0) return 'first';
  if (index === count - 1) return 'last';
  return 'middle';
}

export function calibrationDeviceTreeRowEstimate(
  row: CalibrationDeviceTreeRow | undefined,
): number {
  if (row?.kind === 'station') return 32;
  if (row?.kind === 'device') return 30;
  return 42;
}

export function calibrationDeviceTreeRows(
  nodes: readonly CalibrationDeviceTreeNode[],
): CalibrationDeviceTreeRow[] {
  return nodes.flatMap((node) => {
    const stationRow: CalibrationDeviceTreeRow = {
      kind: 'station',
      key: `station:${node.station.id}`,
      station: node.station,
      expanded: node.expanded,
    };
    if (!node.expanded || !node.branch) return [stationRow];

    const branchRows: CalibrationDeviceTreeRow[] =
      node.branch.state === 'ready'
        ? node.branch.devices.map((device, index) => ({
            kind: 'device',
            key: `device:${node.station.id}:${device.deviceId}`,
            stationId: node.station.id,
            device,
            deviceIndex: index,
            deviceCount: node.branch?.devices.length ?? 0,
            edge: calibrationDeviceTreeEdge(index, node.branch?.devices.length ?? 0),
          }))
        : [
            {
              kind: 'device-state',
              key: `device-state:${node.station.id}:${node.branch.state}`,
              stationId: node.station.id,
              state: node.branch.state,
              edge: 'only',
            },
          ];
    if (node.branch.showFooter) {
      branchRows.push({
        kind: 'device-footer',
        key: `device-footer:${node.station.id}`,
        stationId: node.station.id,
        edge: 'last',
      });
    }
    return [stationRow, ...branchRows];
  });
}

export function visibleCalibrationDevicePaginationCandidate(
  rows: readonly CalibrationDeviceTreeRow[],
  virtualItems: readonly { index: number }[],
): { stationId: string; deviceIndex: number; deviceCount: number } | undefined {
  for (const item of [...virtualItems].reverse()) {
    const row = rows[item.index];
    if (row?.kind === 'device' && row.deviceIndex >= row.deviceCount - 8) {
      return {
        stationId: row.stationId,
        deviceIndex: row.deviceIndex,
        deviceCount: row.deviceCount,
      };
    }
  }
  return undefined;
}
